import React, { useState, useCallback, useEffect } from 'react';
import { Select, Card, Typography, Empty, Tabs, Table, Tag, Button, message } from 'antd';
import { UserOutlined, EyeOutlined, PictureOutlined, VideoCameraOutlined } from '@ant-design/icons';
import api from '../api/axios';
import { formatStudentLabel } from '../utils/studentLabel';
import StudentMediaTab from '../components/student/StudentMediaTab';

const { Text } = Typography;

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Son yüklenen medyalar — şube geneli.
 *
 * Stajyerler yükledikleri medyayı unutup aynı dosyayı ikinci kez
 * yükleyebiliyordu; burada kimin, hangi öğrenciye, ne zaman ne yüklediği
 * görünüyor. Sadece görüntüleme — silme yetkisi bu rolde yok.
 */
const RecentMediaTable = ({ reloadKey }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });

  const fetchRecent = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get('/media/recent', { params: { page, per_page: 25 } });
      setData(res.data.data || []);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch {
      message.error('Son yüklenenler getirilemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  // reloadKey her yüklemeden sonra ve sekmeye her girişte artıyor —
  // liste her zaman güncel olsun.
  useEffect(() => { fetchRecent(1); }, [fetchRecent, reloadKey]);

  const columns = [
    {
      title: 'Tür',
      width: 70,
      render: (_, r) => r.type === 'photo'
        ? <Tag icon={<PictureOutlined />} color="blue">Foto</Tag>
        : <Tag icon={<VideoCameraOutlined />} color="purple">Video</Tag>,
    },
    { title: 'Öğrenci', dataIndex: 'student_name', render: (v) => v || '—' },
    { title: 'Dosya', dataIndex: 'original_name', ellipsis: true },
    { title: 'Boyut', width: 90, render: (_, r) => formatBytes(r.file_size) },
    { title: 'Yükleyen', dataIndex: 'uploader', render: (v) => v || '—' },
    { title: 'Tarih', dataIndex: 'uploaded_at', width: 140 },
    {
      title: 'İşlem',
      width: 100,
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          href={r.stream_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Görüntüle
        </Button>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      size="small"
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: false,
        onChange: (page) => fetchRecent(page),
      }}
      locale={{ emptyText: 'Henüz medya yüklenmemiş.' }}
    />
  );
};

const InternMedia = () => {
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [reloadKey, setReloadKey] = useState(0);

  const searchStudents = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get('/students', { params: { search: q, per_page: 20, status: ['active', 'candidate'] } });
      setResults(res.data.data || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  // Sekmeye her girişte listeyi tazele (araya başka personel yükleme yapmış olabilir)
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'recent') setReloadKey(k => k + 1);
  };

  const items = [
    {
      key: 'upload',
      label: 'Yükle',
      children: (
        <>
          <Card className="mb-4">
            <label className="block text-sm font-medium mb-1">Öğrenci</label>
            <Select
              showSearch
              style={{ width: '100%', maxWidth: 420 }}
              placeholder="Öğrenci adı yazın..."
              filterOption={false}
              onSearch={searchStudents}
              onChange={(val) => setSelected(results.find(s => s.id === val) || null)}
              loading={searching}
              notFoundContent={search.length < 2 ? 'En az 2 karakter girin' : 'Öğrenci bulunamadı'}
              value={selected?.id || undefined}
              allowClear
              onClear={() => setSelected(null)}
              suffixIcon={<UserOutlined />}
            >
              {results.map(s => (
                <Select.Option key={s.id} value={s.id}>
                  {formatStudentLabel(s)}
                </Select.Option>
              ))}
            </Select>
          </Card>

          {selected ? (
            <Card title={formatStudentLabel(selected)}>
              <StudentMediaTab
                studentId={selected.id}
                onUploaded={() => setReloadKey(k => k + 1)}
              />
            </Card>
          ) : (
            <Empty description="Yüklemek için önce bir öğrenci seçin." />
          )}
        </>
      ),
    },
    {
      key: 'recent',
      label: 'Son Yüklenenler',
      children: <RecentMediaTable reloadKey={reloadKey} />,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Medya</h2>
        <Text type="secondary">Bir öğrenci seçip fotoğraf/video yükleyin veya son yüklenenleri görün.</Text>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
    </div>
  );
};

export default InternMedia;
